// DESIGN-SOURCE: none — not in Figma — container, renders no surface of its own
/**
 * Container for the per-player action menu in scenario mode.
 *
 * Determines available actions based on tier, reads current action
 * from the planning mode draft store, and opens the appropriate dialog
 * on selection. When an action is already assigned, clicking the badge
 * opens the modal in edit mode (with Cancel Action + Close + Update buttons).
 * Academy players also get a "Remove from Academy" option.
 *
 * Actions are only available when planning mode is active. When viewing
 * a saved scenario (not in planning mode), actions are read-only.
 * @module features/squad-planning/components/ActionMenu
 */

import {
  ArrowLeftRight,
  DollarSign,
  TrendingUp,
  Plus,
} from "lucide-react-native";
import React, { memo, useCallback, useMemo, useState } from "react";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import { useIsMobile } from "@/features/squad-planning/hooks/useIsMobile";
import { useActiveScenarioPermissions } from "@/features/squad-planning/hooks/useActiveScenarioPermissions";
import { useScenarioEditSeed } from "@/features/squad-planning/hooks/useScenarioEditSeed";
import { useSquadPlanningPlayers } from "@/features/squad-planning/hooks/useSquadPlanningPlayers";
import {
  pendingSellDeepLinkVar,
  usePlanningMode,
} from "@/features/squad-planning/stores/planningModeStore";
import { useScenarios } from "@/features/squad-planning/stores/scenarioStore";
import type {
  PlayerAction,
  SquadPlayer,
} from "@/features/squad-planning/types";
import { mapTransitionToAction } from "@/features/squad-planning/utils";
import AddAnnualSalaryModal from "@/features/squad-planning/components/organisms/AddAnnualSalaryModal";
import LoanActionModal from "@/features/squad-planning/components/organisms/LoanActionModal";
import PromoteActionModal from "@/features/squad-planning/components/organisms/PromoteActionModal";
import RemoveAcademyDialog from "@/features/squad-planning/components/molecules/RemoveAcademyDialog";
import SellActionModal from "@/features/squad-planning/components/organisms/SellActionModal";
import SignActionModal from "@/features/squad-planning/components/organisms/SignActionModal";

import ActionMenuView, {
  type ActionMenuTriggerVisibility,
  type ExtraMenuItem,
} from "./ActionMenuView";

/** Actions available per tier */
const FIRST_TEAM_ACTIONS: PlayerAction[] = ["Sell", "Loan"];
const ACADEMY_ACTIONS: PlayerAction[] = ["Promote", "Loan", "Sell"];
const TARGET_ACTIONS: PlayerAction[] = ["Sign"];
/**
 * SE-8409 — a stable empty reference for "no scenario actions" (Current
 * Squad). A fresh `[]` literal every render would make `actionPressHandlers`'
 * `useMemo` below see a changing dependency on every render.
 */
const NO_ACTIONS: PlayerAction[] = [];

/**
 * SE-8409 — resolves the tier-scoped scenario actions and the extra menu
 * items (Remove from Academy, Add Annual Salary) for one player. Pulled out
 * of the component so its branching is scored on its own, not against the
 * component's cognitive-complexity budget.
 * @param isAcademyPlayer - Whether the player is an academy player.
 * @param isTargetPlayer - Whether this is a Targets-tier row.
 * @param isScenarioContext - Whether a scenario is active (planning mode or
 *   viewing a saved scenario) — Sell/Loan/Promote/Sign only ever apply then.
 * @returns The scenario-action list and the extra menu items.
 */
function resolveMenuActions(
  isAcademyPlayer: boolean,
  isTargetPlayer: boolean,
  isScenarioContext: boolean
): { actions: PlayerAction[]; extraMenuItems: ExtraMenuItem[] } {
  const actions = !isScenarioContext
    ? NO_ACTIONS
    : isTargetPlayer
      ? TARGET_ACTIONS
      : isAcademyPlayer
        ? ACADEMY_ACTIONS
        : FIRST_TEAM_ACTIONS;

  const removeAcademyItems: ExtraMenuItem[] =
    isAcademyPlayer && isScenarioContext
      ? [{ label: "Remove from Academy", key: "__remove_academy__" }]
      : [];
  const addAnnualSalaryItems: ExtraMenuItem[] =
    !isAcademyPlayer && !isTargetPlayer
      ? [
          {
            label: "Add Annual Salary",
            key: "__add_annual_salary__",
            destructive: false,
          },
        ]
      : [];

  return {
    actions,
    extraMenuItems: [...removeAcademyItems, ...addAnnualSalaryItems],
  };
}

/** Icon for each action badge */
const ACTION_ICON: Record<PlayerAction, typeof DollarSign> = {
  Sell: DollarSign,
  Loan: ArrowLeftRight,
  Promote: TrendingUp,
  Sign: DollarSign,
};

/** Display labels for actions in badge state */
const ACTION_BADGE_LABELS: Record<PlayerAction, string> = {
  Sell: "Sold",
  Loan: "Loaned",
  Promote: "Promoted",
  Sign: "Signed",
};

/**
 * Colour token per action badge (SE-5344): Sell red, Sign green, Loan
 * orange/warning, Promote blue accent.
 */
const ACTION_BADGE_COLOR: Record<PlayerAction, string> = {
  Sell: "text-status-error",
  Sign: "text-status-success",
  Loan: "text-status-warning",
  Promote: "text-accent-primary",
};

/** Props for {@link ActionMenuContainer} */
interface ActionMenuContainerProps {
  /** Player data for this row */
  readonly player: SquadPlayer;
  /** Tier name to determine available actions */
  readonly tierName?: string;
  /**
   * How the desktop trigger presents itself at rest. Required, with no
   * default: a ghost trigger depends on its host's interaction styling.
   * SE-6574 shipped an invisible CTA because that dependency was implicit.
   * Hosts with compatible row interaction styling pass `"ghost"`;
   * hosts without one pass `"always"`.
   */
  readonly triggerVisibility: ActionMenuTriggerVisibility;
}

/**
 * Container managing action menu state and dialog lifecycle.
 * @param root0 - Component props
 * @param root0.player - Player data
 * @param root0.tierName - Tier name for action filtering
 * @param root0.triggerVisibility - Ghost reveal vs always-visible trigger
 */
const ActionMenuContainer = ({
  player,
  tierName,
  triggerVisibility,
}: ActionMenuContainerProps) => {
  const isMobile = useIsMobile();
  const {
    isActive: isPlanningModeActive,
    draftActions,
    draftDetails,
    originalDetails,
    removedDetails,
    addDraftAction,
    removeDraftAction,
    enterEditMode,
  } = usePlanningMode();

  const { activeScenario, activeId } = useScenarios();
  // SE-8356: a VIEWER on a shared scenario gets no menu and no editable
  // badge — the action, if any, reads as plain text (hidden, not disabled).
  const { canEdit } = useActiveScenarioPermissions();
  const { players: allPlayers, rawTargets = [] } = useSquadPlanningPlayers();

  // Seed maps for auto-entering edit mode on a saved scenario (SE-5567 —
  // the derivation is shared so no call site can drift into empty maps).
  const {
    actionsMap: scenarioActionsMap,
    detailsMap: scenarioDetailsMap,
    signedPlayersMap: scenarioSignedPlayersMap,
    isReady: isScenarioSeedReady,
  } = useScenarioEditSeed(
    allPlayers,
    rawTargets,
    activeId,
    isPlanningModeActive
  );

  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  // SE-5570: desktop dropdown open state (controlled DesktopDropdownMenu)
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const handleToggleDesktopMenu = useCallback(
    () => setDesktopMenuOpen(prev => !prev),
    []
  );
  const handleCloseDesktopMenu = useCallback(
    () => setDesktopMenuOpen(false),
    []
  );
  // SE-8786: "Model this sale" seeds this player's Sell modal open from
  // the deep link, exactly once — `pendingSellDeepLinkVar` is read here,
  // at mount, never synced afterwards (the same "seed, don't sync"
  // discipline `ObjectivesButtonContainer` documents for its own one-shot
  // deep-link params). It is NOT cleared here: clearing happens from the
  // close/confirm/cancel handlers below, so a reader who has not yet acted
  // on it is unaffected by an unrelated re-render of this row.
  const [sellModalOpen, setSellModalOpen] = useState(
    () => pendingSellDeepLinkVar() === player.id
  );
  const [loanModalOpen, setLoanModalOpen] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [removeAcademyOpen, setRemoveAcademyOpen] = useState(false);
  // SE-8409: "Add Annual Salary" is a direct write, not a scenario draft
  // action — it has no place in `modalOpeners`'/`actions`' PlayerAction
  // machinery below, so it gets its own open state and handler.
  const [addAnnualSalaryOpen, setAddAnnualSalaryOpen] = useState(false);

  // Read action from draft (planning mode) or saved scenario (transitionType)
  const savedAction = mapTransitionToAction(player.transitionType);
  const currentAction = isPlanningModeActive
    ? (draftActions[player.id] ?? null)
    : savedAction;
  const currentDetails = isPlanningModeActive
    ? (draftDetails[player.id] ??
      originalDetails?.[player.id] ??
      scenarioDetailsMap[player.id] ??
      null)
    : (scenarioDetailsMap[player.id] ?? null);
  const isEditing = currentAction != null;
  const isAcademyPlayer = player.isAcademy;

  /**
   * Returns currentDetails only when the active action matches.
   * @param action - The action type to check
   * @returns Details if action matches, null otherwise
   */
  const detailsFor = (action: PlayerAction) => {
    if (currentAction === action) return currentDetails;
    // Fallback: last edited values (removed) → original saved values
    const removed = removedDetails?.[player.id];
    if (removed?.action === action) return removed;
    const original = originalDetails?.[player.id];
    return original?.action === action ? original : null;
  };

  const isTargetPlayer = tierName === "Targets";
  // SE-8409: "Add Annual Salary" is available for a first-team player
  // whether or not a scenario is active — Sell/Loan/Promote/Sign are
  // scenario draft actions and stay scenario-only. See `resolveMenuActions`.
  const isScenarioContext = isPlanningModeActive || activeId != null;
  const { actions, extraMenuItems } = useMemo(
    () =>
      resolveMenuActions(isAcademyPlayer, isTargetPlayer, isScenarioContext),
    [isAcademyPlayer, isTargetPlayer, isScenarioContext]
  );

  const handleRemoveAcademyPress = useCallback(() => {
    setRemoveAcademyOpen(true);
  }, []);

  const handleRemoveAcademyClose = useCallback(() => {
    setRemoveAcademyOpen(false);
  }, []);

  const handleAddAnnualSalaryPress = useCallback(() => {
    setAddAnnualSalaryOpen(true);
  }, []);

  const handleAddAnnualSalaryClose = useCallback(() => {
    setAddAnnualSalaryOpen(false);
  }, []);

  /** Lookup table mapping each action to its modal opener */
  const modalOpeners: Record<PlayerAction, () => void> = useMemo(
    () => ({
      Sell: () => setSellModalOpen(true),
      Loan: () => setLoanModalOpen(true),
      Sign: () => setSignModalOpen(true),
      Promote: () => setPromoteModalOpen(true),
    }),
    []
  );

  /**
   * When viewing a saved scenario and user clicks an action, auto-enter
   * edit mode with the scenario's current state, then open the modal.
   */
  const handleActionWithAutoEdit = useCallback(
    (action: PlayerAction) => {
      // A cold reload fetches the scenario over the network; seeding from the
      // maps available mid-flight replaces the draft with nothing, which the
      // next updateScenario persists as a deletion (SE-6519).
      if (!isScenarioSeedReady) return;
      if (!isPlanningModeActive && activeId != null && activeScenario != null) {
        // Enter edit mode seeded with the saved scenario's actions and details
        enterEditMode(
          activeId,
          scenarioActionsMap,
          scenarioDetailsMap,
          scenarioSignedPlayersMap
        );
      }
      setActionSheetOpen(false);
      modalOpeners[action]();
    },
    [
      isScenarioSeedReady,
      isPlanningModeActive,
      activeId,
      activeScenario,
      enterEditMode,
      scenarioActionsMap,
      scenarioDetailsMap,
      scenarioSignedPlayersMap,
      modalOpeners,
    ]
  );

  const actionPressHandlers = useMemo(
    () =>
      Object.fromEntries(
        actions.map(action => [action, () => handleActionWithAutoEdit(action)])
      ),
    [actions, handleActionWithAutoEdit]
  );

  /** Combined handlers for both scenario actions and extra items */
  const allPressHandlers = useMemo(
    () => ({
      ...actionPressHandlers,
      __remove_academy__: handleRemoveAcademyPress,
      __add_annual_salary__: handleAddAnnualSalaryPress,
    }),
    [actionPressHandlers, handleRemoveAcademyPress, handleAddAnnualSalaryPress]
  );

  const handleBadgePress = useCallback(() => {
    if (currentAction == null) return;
    if (!isScenarioSeedReady) return;
    if (!isPlanningModeActive && activeId != null) {
      enterEditMode(
        activeId,
        scenarioActionsMap,
        scenarioDetailsMap,
        scenarioSignedPlayersMap
      );
    }
    modalOpeners[currentAction]();
  }, [
    currentAction,
    isScenarioSeedReady,
    isPlanningModeActive,
    activeId,
    enterEditMode,
    scenarioActionsMap,
    scenarioDetailsMap,
    scenarioSignedPlayersMap,
    modalOpeners,
  ]);

  const handleOpenActionSheet = useCallback(() => {
    setActionSheetOpen(true);
  }, []);

  const handleCloseActionSheet = useCallback(() => {
    setActionSheetOpen(false);
  }, []);

  // SE-8786: the Sell modal's three exits (confirm, close, cancel) are also
  // the three places `pendingSellDeepLinkVar` can safely clear — see the
  // seed above for why clearing is NOT done there instead. Gated on this
  // being the player the var actually names, so one row's close can never
  // clear a DIFFERENT, not-yet-mounted row's still-pending signal.
  const clearPendingSellDeepLink = useCallback(() => {
    if (pendingSellDeepLinkVar() === player.id) pendingSellDeepLinkVar(null);
  }, [player.id]);

  const handleSellConfirm = useCallback(
    (salePrice: number | null) => {
      addDraftAction(player.id, "Sell", {
        action: "Sell",
        transferFee: null,
        annualSalary: null,
        durationMonths: null,
        signingBonus: null,
        agentFee: null,
        salePrice,
        loanFee: null,
        coveredSharePercent: null,
      });
      setSellModalOpen(false);
      clearPendingSellDeepLink();
    },
    [player.id, addDraftAction, clearPendingSellDeepLink]
  );

  const handleLoanConfirm = useCallback(
    (
      loanFee: number | null,
      durationMonths: number | null,
      coveredSharePercent: number | null
    ) => {
      addDraftAction(player.id, "Loan", {
        action: "Loan",
        transferFee: null,
        annualSalary: null,
        durationMonths,
        signingBonus: null,
        agentFee: null,
        salePrice: null,
        loanFee,
        coveredSharePercent,
      });
      setLoanModalOpen(false);
    },
    [player.id, addDraftAction]
  );

  const handleCancelAction = useCallback(() => {
    removeDraftAction(player.id);
    setSellModalOpen(false);
    setLoanModalOpen(false);
    setSignModalOpen(false);
    setPromoteModalOpen(false);
    clearPendingSellDeepLink();
  }, [player.id, removeDraftAction, clearPendingSellDeepLink]);

  const handleSellClose = useCallback(() => {
    setSellModalOpen(false);
    clearPendingSellDeepLink();
  }, [clearPendingSellDeepLink]);
  const handleLoanClose = useCallback(() => setLoanModalOpen(false), []);

  const handleSignConfirm = useCallback(
    (
      transferFee: number | null,
      annualSalary: number | null,
      durationMonths: number | null,
      signingBonus: number | null,
      agentFee: number | null
    ) => {
      addDraftAction(player.id, "Sign", {
        action: "Sign",
        transferFee,
        annualSalary,
        durationMonths,
        signingBonus,
        agentFee,
        salePrice: null,
        loanFee: null,
        coveredSharePercent: null,
      });
      setSignModalOpen(false);
    },
    [player.id, addDraftAction]
  );

  const handleSignClose = useCallback(() => setSignModalOpen(false), []);

  const handlePromoteConfirm = useCallback(
    (annualSalary: number | null, durationMonths: number | null) => {
      addDraftAction(player.id, "Promote", {
        action: "Promote",
        transferFee: null,
        annualSalary,
        durationMonths,
        signingBonus: null,
        agentFee: null,
        salePrice: null,
        loanFee: null,
        coveredSharePercent: null,
      });
      setPromoteModalOpen(false);
    },
    [player.id, addDraftAction]
  );

  const handlePromoteClose = useCallback(() => setPromoteModalOpen(false), []);

  // Show the action menu when in planning mode, viewing a saved scenario, or
  // (SE-8409) on the Current Squad for a first-team player — where the only
  // available item is "Add Annual Salary".
  const canShowActions =
    isScenarioContext || (!isAcademyPlayer && !isTargetPlayer);

  if (!canShowActions) return null;

  const menuElement = isMobile ? (
    <>
      <Pressable
        onPress={handleOpenActionSheet}
        testID="add-action-trigger-mobile"
      >
        <Stack direction="horizontal" className="items-center gap-micro">
          <Icon as={Plus} className="text-accent-primary" size="2xs" />
          <Text
            variant="unstyled"
            className="font-medium text-caption text-accent-primary"
          >
            Add action
          </Text>
        </Stack>
      </Pressable>
      <ActionMenuView
        isMobile
        isActionSheetOpen={actionSheetOpen}
        onCloseActionSheet={handleCloseActionSheet}
        actions={actions}
        actionPressHandlers={allPressHandlers}
        extraMenuItems={extraMenuItems}
      />
    </>
  ) : (
    <ActionMenuView
      actions={actions}
      actionPressHandlers={allPressHandlers}
      extraMenuItems={extraMenuItems}
      isMenuOpen={desktopMenuOpen}
      onToggleMenu={handleToggleDesktopMenu}
      onCloseMenu={handleCloseDesktopMenu}
      triggerVisibility={triggerVisibility}
    />
  );

  if (!canEdit) {
    return isEditing ? (
      <Stack
        direction="horizontal"
        testID="action-badge-readonly"
        className="min-h-11 items-center gap-1"
      >
        <Icon
          as={ACTION_ICON[currentAction]}
          className={ACTION_BADGE_COLOR[currentAction]}
          size="xs"
        />
        <Text
          variant="caption-strong"
          className={ACTION_BADGE_COLOR[currentAction]}
        >
          {ACTION_BADGE_LABELS[currentAction]}
        </Text>
      </Stack>
    ) : null;
  }

  return (
    <>
      {isEditing ? (
        <Pressable
          onPress={handleBadgePress}
          testID="action-badge"
          // Explicit ≥44pt tap target — a hitSlop expansion can be clipped by
          // the badge's immediate wrapper, so size the pressable itself (SE-5355)
          className="min-h-11 min-w-11 items-center justify-center"
        >
          <Stack direction="horizontal" className="items-center gap-1">
            <Icon
              as={ACTION_ICON[currentAction]}
              className={ACTION_BADGE_COLOR[currentAction]}
              size="xs"
            />
            <Text
              variant="caption-strong"
              className={ACTION_BADGE_COLOR[currentAction]}
            >
              {ACTION_BADGE_LABELS[currentAction]}
            </Text>
          </Stack>
        </Pressable>
      ) : (
        menuElement
      )}
      <SellActionModal
        key={`${player.id}-sell-${String(isPlanningModeActive)}-${String(sellModalOpen)}`}
        isOpen={sellModalOpen}
        onClose={handleSellClose}
        onConfirm={handleSellConfirm}
        onCancelAction={isEditing ? handleCancelAction : undefined}
        playerId={player.id}
        playerName={player.displayName}
        initialValue={detailsFor("Sell")?.salePrice ?? null}
        isEditMode={currentAction === "Sell"}
      />
      <LoanActionModal
        key={`${player.id}-loan-${String(isPlanningModeActive)}-${String(loanModalOpen)}`}
        isOpen={loanModalOpen}
        onClose={handleLoanClose}
        onConfirm={handleLoanConfirm}
        onCancelAction={isEditing ? handleCancelAction : undefined}
        playerId={player.id}
        playerName={player.displayName}
        initialLoanFee={detailsFor("Loan")?.loanFee ?? null}
        initialDuration={detailsFor("Loan")?.durationMonths ?? null}
        initialCoveredSharePercent={
          detailsFor("Loan")?.coveredSharePercent ?? null
        }
        isEditMode={currentAction === "Loan"}
      />
      <SignActionModal
        key={`${player.id}-sign-${String(isPlanningModeActive)}-${String(signModalOpen)}`}
        isOpen={signModalOpen}
        onClose={handleSignClose}
        onConfirm={handleSignConfirm}
        onCancelAction={isEditing ? handleCancelAction : undefined}
        playerId={player.id}
        playerName={player.displayName}
        initialTransferFee={detailsFor("Sign")?.transferFee ?? null}
        initialAnnualSalary={detailsFor("Sign")?.annualSalary ?? null}
        initialDuration={detailsFor("Sign")?.durationMonths ?? null}
        initialSigningBonus={detailsFor("Sign")?.signingBonus ?? null}
        initialAgentFee={detailsFor("Sign")?.agentFee ?? null}
        isEditMode={currentAction === "Sign"}
      />
      <PromoteActionModal
        key={`${player.id}-promote-${String(isPlanningModeActive)}-${String(promoteModalOpen)}`}
        isOpen={promoteModalOpen}
        onClose={handlePromoteClose}
        onConfirm={handlePromoteConfirm}
        onCancelAction={isEditing ? handleCancelAction : undefined}
        playerId={player.id}
        playerName={player.displayName}
        initialAnnualSalary={detailsFor("Promote")?.annualSalary ?? null}
        initialDuration={detailsFor("Promote")?.durationMonths ?? null}
        isEditMode={currentAction === "Promote"}
      />
      <RemoveAcademyDialog
        isOpen={removeAcademyOpen}
        playerId={player.id}
        playerName={player.displayName}
        onClose={handleRemoveAcademyClose}
      />
      <AddAnnualSalaryModal
        key={`${player.id}-annual-salary-${String(addAnnualSalaryOpen)}`}
        isOpen={addAnnualSalaryOpen}
        onClose={handleAddAnnualSalaryClose}
        playerId={player.id}
        playerName={player.displayName}
        initialAnnualSalary={player.wage}
      />
    </>
  );
};

ActionMenuContainer.displayName = "ActionMenuContainer";

export default memo(ActionMenuContainer);
